import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import API_BASE_URL from './api';
import { SUPPORTED_LANGS, getLangFromPath, buildPathWithLang } from './i18n/lang';

const AuthContext = createContext({
  user: null,
  isAuth: false,
  loading: true,
  login: async () => ({ ok: false }),
  logout: async () => {},
  refreshProfile: async () => null,
});

function normaliseUser(data) {
  if (!data) return null;
  const { email = '', name = '', is_confirmed, confirmed, locale } = data;
  return {
    email,
    name,
    confirmed: typeof confirmed === 'boolean' ? confirmed : Boolean(is_confirmed),
    locale,
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const apiBase = useMemo(() => (API_BASE_URL || '').replace(/\/+$/, ''), []);
  const pendingProfileRef = useRef(false);

  const refreshProfile = useCallback(async () => {
  if (pendingProfileRef.current) return;
  pendingProfileRef.current = true;
  try {
    const response = await fetch(`${apiBase}/profile`, {
      method: 'GET',
      credentials: 'include',
      mode: 'cors',
    });
    if (!response.ok) {
      if (response.status === 401) {
        setUser(null);
      }
      return null;
    }
    const data = await response.json();
    const nextUser = normaliseUser(data);
    setUser(nextUser);
    return nextUser;
  } catch (err) {
    console.warn('AuthProvider: failed to fetch profile', err);
    setUser(null);
    return null;
  } finally {
    pendingProfileRef.current = false;
  }
}, [apiBase]);


  const login = useCallback(async ({ email, password }) => {
    setLoading(true);
    try {
      const response = await fetch(`${apiBase}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        mode: 'cors',
        body: JSON.stringify({ email, password }),
      });
      const payload = await response.json().catch(() => ({}));
      console.log('[RM][login] status=', response.status, 'payload=', payload);
      if (!response.ok) {
        return { ok: false, status: response.status, payload };
      }
      const profile = await refreshProfile();
      if (profile) {
        window.dispatchEvent(new Event('cart:refresh'));
      }
      return { ok: true, status: response.status, payload };
    } catch (err) {
      console.warn('AuthProvider: login failed', err);
      return { ok: false, error: err };
    } finally {
      setLoading(false);
    }
  }, [apiBase, refreshProfile]);

  const logout = useCallback(async () => {
    setLoading(true);
    try {
      await fetch(`${apiBase}/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (err) {
      console.warn('AuthProvider: logout failed', err);
    } finally {
      setUser(null);
      setLoading(false);
      window.dispatchEvent(new Event('cart:refresh'));
    }
  }, [apiBase]);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      await refreshProfile();
      if (isMounted) setLoading(false);
    })();
    return () => { isMounted = false; };
  }, [refreshProfile]);

  useEffect(() => {
    const onUnauthorized = () => {
      setUser(null);
      window.dispatchEvent(new Event('cart:refresh'));
    };
    window.addEventListener('auth:unauthorized', onUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', onUnauthorized);
  }, []);

  useEffect(() => {
    if (!user?.locale) return;

    const desired = user.locale;
    if (!SUPPORTED_LANGS.includes(desired)) return;

    const { pathname, search, hash } = window.location;
    const current = getLangFromPath(pathname);
    if (current === desired) return;

    const noLangPath = pathname.replace(/^\/(uk|en|it|fr)(\/|$)/, '/');
    const target = buildPathWithLang(desired, noLangPath) + search + hash;

    window.location.replace(target);
  }, [user?.locale]);

  const value = useMemo(() => ({
    user,
    isAuth: Boolean(user),
    loading,
    login,
    logout,
    refreshProfile,
  }), [user, loading, login, logout, refreshProfile]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
